import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { ISession } from "../../../../../types/dashboard";
import { FullSessionItem } from "../fullSessionItem/FullSessionItem";
import { useNavigate } from "react-router-dom";
import { Spinner } from "../../../../../components/spinner/Spinner";
import { DashboardContext } from "../../../DashboardContext";
import { GoodScroll } from "../../../../desktopPage/GoodScroll";
import { AppContext } from "../../../../../appContext";
import { formatDate3, formatDateDayMonth } from "../../../../../utils/dateFormats";
import { DateLine, DateLineThin } from "../../../../../components/dateLine/DateLine";
import { Collapser } from "../../../../../components/collapser/Collapser";
import { DropDownButton, DropDownButtonColored } from "../../../../../components/dropDown/DropDownButton";
import { usePermanentState } from "../../../../../utils/usePermanentState";
import { getSessions } from "../../../../../api/sessionApi";
import { serverToClientSession } from "../../../../../api/sessionApi";
import { parseTimezoneAwareDatetime } from "../../../../../utils/timezone";
import './FullSessionList.css';

interface ICollapserGroupData{
    sessions: ISession[],
    date: Date,
    open?: boolean
}

interface IUploadFileForm {
    sessionId: string,
    onProgress: (current: number, total: number)=>void
}

const UploadFileForm = ({sessionId, onProgress}: IUploadFileForm) => {
    const { uploadSessionFiles } = useContext(AppContext);
    const ref = useRef<HTMLInputElement>(null);
    const sendFile = (selectedFiles: FileList)=>{
        /*sendFiles(selectedFiles, sessionId, onProgress).then((e)=>{
            console.log('upload', e);
            onProgress(100, 100);
        });*/
        uploadSessionFiles(selectedFiles, sessionId);
    };

    useEffect(()=>{
        if (ref.current){
            ref.current.click();
        }
    }, [ref]);

    return <div style={{display:'none'}}>
        <input ref={ref} type="file" onChange={(e)=>{
            sendFile(e.target.files);
        }}/>
    </div>
}

const GroupCollapser = ({collapserId, defaultOpen, label, children, isBig} : {collapserId: string, defaultOpen: boolean, label: string, isBig: boolean} & React.PropsWithChildren)=>{
    // Для Upcoming будем принудительно устанавливать 'close' при инициализации
    const initialState = label === "Upcoming" ? 'close' : 'default';

    const [collapsedState, setCollapsedState] = usePermanentState<('open'|'close'|'default')>(collapserId, initialState);

    // Для секции Upcoming состояние управляется отдельно с помощью isUpcomingOpen

    const isOpen = (last: 'open'|'close'|'default') => {
        if (label === "Upcoming" && last === 'default') {
            return false; // Для Upcoming всегда закрыто по умолчанию
        }
        return (last === 'open' || (last === 'default' && defaultOpen));
    };

    const DateLineComponent = isBig ? DateLine : DateLineThin;
    return <div className="FullSessionList_group">
        <DateLineComponent className="FullSessionList_groupLabel" 
            onClick={()=>{
                setCollapsedState((last)=> (isOpen(last) ? 'close' : 'open'));
            }}
            label={
            <>
                <span>{label}</span>
                <DropDownButtonColored isActive={true} 
                    isOpen={isOpen(collapsedState)}
                    onChangeOpen={()=>{}}
                    ></DropDownButtonColored>
            </>
        }></DateLineComponent>
        <Collapser className="FullSessionList_collapser" collapsed={!isOpen(collapsedState)}>
            <div className="FullSessionList_collapserGroup">
                {children}
                {/*{group.sessions.map((session)=>renderFullSessionItem(session))*/}
            </div>
        </Collapser>
    </div>
}

interface IFullSessionListProps{
    onClose: ()=>void;
    onSave: (data: string)=>void;
    onSaveDraft: ()=>void;
    sessions: ISession[],
    onEdit: (session:ISession)=>void;
    onAdd: ()=>void;
}

// Global reference to trigger client session list reload
let clientSessionReloadTrigger: (() => void) | null = null;

// Utility function to clear client session cache - can be called from other components
export const clearClientSessionCache = (clientId: string) => {
    localStorage.removeItem(`cached_client_sessions_${clientId}`);
    localStorage.removeItem(`cached_client_sessions_timestamp_${clientId}`);
    // Trigger reload if component is mounted and not using sessions from props
    if (clientSessionReloadTrigger) {
        clientSessionReloadTrigger();
    }
};

export const FullSessionList = ({onClose, sessions, onEdit, onAdd}: IFullSessionListProps)=>{
    const [uploadSession, setUploadSession] = useState('');
    const [clickFix, setClickFix] = useState(0);
    const [allSessions, setAllSessions] = useState<ISession[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [lastDate, setLastDate] = useState<string>('');
    const [lastId, setLastId] = useState<string>('');
    const [hasMore, setHasMore] = useState(true);
    const [cacheVersion, setCacheVersion] = useState(0);
    const navigate = useNavigate();
    const {progresses, translate} = useContext(AppContext);
    const {selectedClient} = useContext(DashboardContext);
    
    const [openSessionIdsArray, setOpenSessionIdsArray] = usePermanentState<string[]>(`openSessionIds:${selectedClient?.id || 'default'}`, []);

    // Состояние для секции Upcoming - сохраняется между обновлениями
    const [isUpcomingOpen, setIsUpcomingOpen] = usePermanentState<boolean>(`isUpcomingOpen:${selectedClient?.id || 'default'}`, false);

    useEffect(() => {
        // Only load from server if we don't have sessions from props
        if (selectedClient && (!sessions || sessions.length === 0)) {
            setLoading(true);
            loadClientSessions(false);
        } else if (sessions && sessions.length > 0) {
            setLoading(false);
            // When sessions are provided via props, don't use cached sessions
            setAllSessions([]);
        }
    }, [selectedClient, cacheVersion, sessions]);

    // Set up the reload trigger
    useEffect(() => {
        clientSessionReloadTrigger = () => {
            // Only trigger reload if we're not using sessions from props
            if (!sessions || sessions.length === 0) {
                setCacheVersion(prev => prev + 1);
            }
        };

        return () => {
            clientSessionReloadTrigger = null;
        };
    }, [sessions]);

    // Load sessions with pagination
    useEffect(() => {
        // Try to load from cache
        const cachedSessions = localStorage.getItem(`cached_client_sessions_${selectedClient.id}`);
        const cacheTimestamp = localStorage.getItem(`cached_client_sessions_timestamp_${selectedClient.id}`);
        const now = Date.now();

        // If cache is fresh (less than 5 minutes), use it
        if (cachedSessions && cacheTimestamp && (now - parseInt(cacheTimestamp)) < 5 * 60 * 1000) {
            try {
                const parsed = JSON.parse(cachedSessions);
                // Check if cache has the new format with pagination state
                if (parsed.sessions && parsed.lastDate !== undefined && parsed.lastId !== undefined && parsed.hasMore !== undefined) {
                    setAllSessions(parsed.sessions);
                    setLastDate(parsed.lastDate);
                    setLastId(parsed.lastId);
                    setHasMore(parsed.hasMore);
                    setLoading(false);
                    return;
                } else {
                    // Old cache format, just use sessions - clear it and load fresh
                    localStorage.removeItem(`cached_client_sessions_${selectedClient.id}`);
                    localStorage.removeItem(`cached_client_sessions_timestamp_${selectedClient.id}`);
                }
            } catch (e) {
                // If parsing error, clear cache and load fresh data
                localStorage.removeItem(`cached_client_sessions_${selectedClient.id}`);
                localStorage.removeItem(`cached_client_sessions_timestamp_${selectedClient.id}`);
            }
        }

        loadClientSessions();
    }, [selectedClient.id, cacheVersion]); // Reload when client changes or cache version changes

    // isUpcomingOpen now persists via usePermanentState - no need to reset on allSessions change

    const loadClientSessions = useCallback(async (isLoadMore = false) => {
        try {
            if (isLoadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
                setAllSessions([]);
                setLastDate('');
                setLastId('');
                setHasMore(true);
            }

            // Get sessions with pagination
            const sessionsResponse = await getSessions(
                selectedClient.id,
                20, // limit to 20 sessions as requested
                isLoadMore ? lastDate : undefined,
                isLoadMore ? lastId : undefined
            );

            if (!sessionsResponse.ok) {
                throw new Error('Failed to load client sessions');
            }

            const rawSessions = await sessionsResponse.json();

            // Convert sessions to the needed format
            const clientSessions = rawSessions.map((sessionWrapper: any) => {
                const sessionId = Object.keys(sessionWrapper)[0];
                const sessionData = sessionWrapper[sessionId];
                return serverToClientSession({
                    ...sessionData,
                    id: sessionId
                });
            });

            // Update pagination state
            if (clientSessions.length > 0) {
                const lastSession = clientSessions[clientSessions.length - 1];
                setLastDate(lastSession.date);
                setLastId(lastSession.id);
            }

            // Check if we have more data
            setHasMore(clientSessions.length === 20);

            // Update sessions list
            if (isLoadMore) {
                const newAllSessions = [...allSessions, ...clientSessions];
                setAllSessions(newAllSessions);
                // Update cache with new combined sessions
                const cacheData = {
                    sessions: newAllSessions,
                    lastDate: clientSessions.length > 0 ? clientSessions[clientSessions.length - 1].date : lastDate,
                    lastId: clientSessions.length > 0 ? clientSessions[clientSessions.length - 1].id : lastId,
                    hasMore: clientSessions.length === 20
                };
                localStorage.setItem(`cached_client_sessions_${selectedClient.id}`, JSON.stringify(cacheData));
                localStorage.setItem(`cached_client_sessions_timestamp_${selectedClient.id}`, Date.now().toString());
            } else {
                setAllSessions(clientSessions);
                // Save to cache for initial load with pagination state
                const cacheData = {
                    sessions: clientSessions,
                    lastDate: clientSessions.length > 0 ? clientSessions[clientSessions.length - 1].date : '',
                    lastId: clientSessions.length > 0 ? clientSessions[clientSessions.length - 1].id : '',
                    hasMore: clientSessions.length === 20
                };
                localStorage.setItem(`cached_client_sessions_${selectedClient.id}`, JSON.stringify(cacheData));
                localStorage.setItem(`cached_client_sessions_timestamp_${selectedClient.id}`, Date.now().toString());
            }
        } catch (error) {
            console.error('Failed to load client sessions:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [selectedClient.id, lastDate, lastId]);

    const scrollRef = useRef<HTMLDivElement>(null);
    const [collapsedState, setCollapsedState] = usePermanentState<('open'|'close'|'default')[]>(selectedClient.id+':'+'collapsedSessions', []);

    const reverseDirection: boolean = true;

    // Use sessions from props if available (for immediate updates), otherwise use cached allSessions
    // When sessions are provided via props and not empty, use them; otherwise use cached sessions
    const effectiveSessions = sessions && sessions.length > 0 ? sessions : allSessions;

    const sessionGroupsByDays: {sessions: ISession[], date: Date}[] = useMemo(()=>{
        if (!effectiveSessions){
            return []; // Return empty array to avoid errors
        }
        const result: {sessions: ISession[], date: Date}[] = [];
        const sorted = [...effectiveSessions].sort((a, b)=>new Date(a.date).valueOf() - new Date(b.date).valueOf());

        sorted.forEach(session=>{
            const lastGroup = result[result.length-1];
            const shouldNew = !lastGroup 
                || ((lastGroup.date.getUTCDate() != (session.startDatetime ? parseTimezoneAwareDatetime(session.startDatetime) : new Date(session.date || 0)).getUTCDate()) 
                || ((session.startDatetime ? parseTimezoneAwareDatetime(session.startDatetime) : new Date(session.date || 0)).valueOf() - lastGroup.date.valueOf() > 24 * 60 * 60 * 1000));
            const currentGroup: {sessions: ISession[], date: Date} = !shouldNew ? lastGroup : {sessions: [], date: session.startDatetime ? parseTimezoneAwareDatetime(session.startDatetime) : new Date(session.date || 0)};
            currentGroup.sessions.push(session);
            if (shouldNew){
                result.push(currentGroup);
            }
        });

        console.log('grouped sessions', sorted, result);
        return result;
    }, [effectiveSessions]);

    // Get the first day of the month for a given date
    const getFirstDayOfMonth = (date?: Date) => {
        const currentDate = date ? new Date(date) : new Date();
        currentDate.setUTCDate(1);
        currentDate.setUTCHours(0);
        currentDate.setUTCMinutes(0);
        currentDate.setUTCSeconds(0);
        currentDate.setUTCMilliseconds(0);
        return currentDate;
    }

    const sortedSessions = useMemo(()=>{
        if (!effectiveSessions){
            return []; //Return empty array to avoid errors
        }
        return [...effectiveSessions].sort((a, b)=>{
            const aDate = a.startDatetime ? parseTimezoneAwareDatetime(a.startDatetime) : new Date(a.date || 0);
            const bDate = b.startDatetime ? parseTimezoneAwareDatetime(b.startDatetime) : new Date(b.date || 0);
            return (reverseDirection ? -1 : 1) * (aDate.valueOf() - bDate.valueOf());
        });
    }, [effectiveSessions]);

    const sessionGroupsByMonths: {sessions: ISession[], date: Date, open?: boolean}[] = useMemo(()=>{
        if (!effectiveSessions){
            return []; //Return empty array to avoid errors
        }
        const result: {sessions: ISession[], date: Date, open: boolean}[] = [];
        const sorted = sortedSessions;

        const currentDate = new Date();
        const currentMonth = currentDate.getUTCMonth();
        const currentYear = currentDate.getUTCFullYear();

        sorted.forEach(session => {
            const sessionDate = session.startDatetime ? parseTimezoneAwareDatetime(session.startDatetime) : new Date(session.date || 0);
            const sessionMonth = sessionDate.getUTCMonth();
            const sessionYear = sessionDate.getUTCFullYear();

            const lastGroup = result[result.length-1];
            const monthDate = getFirstDayOfMonth(sessionDate);

            // Check if we need a new group - either no group exists yet, or this session belongs to a different month
            const shouldNew = !lastGroup 
                || lastGroup.date.getUTCMonth() !== monthDate.getUTCMonth() 
                || lastGroup.date.getUTCFullYear() !== monthDate.getUTCFullYear();

            const isCurrentMonth = sessionMonth === currentMonth && sessionYear === currentYear;

            const currentGroup: {sessions: ISession[], date: Date, open: boolean} = 
                !shouldNew ? lastGroup : {
                    sessions: [], 
                    date: monthDate, 
                    open: isCurrentMonth // Open by default if it's the current month
                };

            currentGroup.sessions.push(session);

            if (shouldNew) {
                result.push(currentGroup);
            }
        });

        console.log('grouped sessions by month', sorted, result);
        return result;
    }, [effectiveSessions, sortedSessions]);

    const sessionGroups = sessionGroupsByMonths;

    const groupUpcoming = ()=>{
        const upcomingGroup: {sessions: ISession[], date: Date, open?: boolean}[] = [];
        const actualGroup: {sessions: ISession[], date: Date, open?: boolean}[] = [];
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();

        // Для каждой месячной группы проверим, есть ли в ней предстоящие сессии
        sessionGroups?.forEach(group => {
            // Создаем копии групп для фильтрации сессий
            const upcomingSessions: ISession[] = [];
            const actualSessions: ISession[] = [];
            const groupDate = new Date(group.date);
            const groupMonth = groupDate.getMonth();
            const groupYear = groupDate.getFullYear();

            // Проверяем, относится ли месяц к будущим
            const isFutureMonth = (groupYear > currentYear) || 
                                 (groupYear === currentYear && groupMonth > currentMonth);

            // Разделяем сессии внутри группы
            group.sessions.forEach(session => {
                const sessionDate = new Date(session.date).valueOf();
                const currentDateTime = currentDate.valueOf();

            // Если месяц будущий - добавляем в upcoming
                if (isFutureMonth && sessionDate > currentDateTime) {
                    upcomingSessions.push(session);
                } else if (sessionDate > currentDateTime) {
                    // Текущий месяц, но дата в будущем
                    upcomingSessions.push(session);
                } else {
                    // Прошедшие сессии
                    actualSessions.push(session);
                }
            });

            // Добавляем группы в соответствующие разделы, если в них есть сессии
            if (upcomingSessions.length > 0) {
                upcomingGroup.push({
                    ...group,
                    sessions: upcomingSessions,
                    // Принудительно устанавливаем свернутость для всех подгрупп Upcoming
                    open: false 
                });
            }

            if (actualSessions.length > 0) {
                actualGroup.push({
                    ...group,
                    sessions: actualSessions,
                    open: false // По умолчанию свернуто
                });
            }
        });

        return [upcomingGroup, actualGroup];
    }

    useEffect(()=>{
        setCollapsedState((last)=>{
            const next: typeof last = [];
            sessionGroups?.forEach((it, i)=>{
                if (!last || !last[i]){
                    next.push('default');
                } else {
                    next.push(last[i]);
                }
            });
            return next;
        });

    }, [sessionGroups]);

    const toggleSessionOpen = useCallback((sessionId: string) => {
        setOpenSessionIdsArray((prev: string[]) => {
            if ((prev || []).includes(sessionId)) {
                return (prev || []).filter(id => id !== sessionId);
            } else {
                return [...(prev || []), sessionId];
            }
        });
    }, [setOpenSessionIdsArray]);

    const renderFullSessionItem = (session: ISession)=>{
        return <FullSessionItem 
            key={session.id}
            isOpen={(openSessionIdsArray || []).includes(session.id)}
            onToggleOpen={() => toggleSessionOpen(session.id)}
            progress={progresses[session.id]}
            data={session} 
            onUpload={()=>{
                setClickFix(last=>last+1);
                setUploadSession(session.id);
            }}
            onWatch={()=>{
                navigate('/meeting/'+ selectedClient.id+ '/'+ session.id);
            }}
            onEdit={()=>onEdit(session)}
        ></FullSessionItem>
    }

    const renderCollapseGrouped = (sessionGroups: {sessions: ISession[], date: Date, open?: boolean}[])=>{
        return sessionGroups.map((group, groupIndex)=>{
            return <GroupCollapser 
                key={`group-${groupIndex}`} 
                defaultOpen={group.open} 
                label={new Date(group.date).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                collapserId={`groupCollapser:date:${new Date(group.date).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}:${selectedClient.id}`}
                isBig={false}
            >
                {group.sessions.map((session, sessionIndex) => renderFullSessionItem(session))} 
            </GroupCollapser>
        })
    }

    const listModes = {
        single: ()=>{
           return !effectiveSessions ? 
              <div className="FullSessionList_spinner"><Spinner /></div>
              : (effectiveSessions.length > 0 ? 
                 sortedSessions.map((session, index)=><React.Fragment key={`session-${session.id || index}`}>{renderFullSessionItem(session)}</React.Fragment>) 
                 : <div className="FullSessionList_empty">Нет сессий</div>);
        },
        grouped: ()=>{
            return !sessionGroups ? 
               <div className="FullSessionList_spinner"><Spinner /></div>
               : (sessionGroups.length > 0 ?
                  sessionGroups.map((group, groupIndex)=>{
                     return <div key={`group-${groupIndex}`} className="FullSessionList_group">
                         <DateLine className="FullSessionList_groupLabel" label={new Date(group.date).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}></DateLine>
                         {group.sessions.map((session, sessionIndex) => <React.Fragment key={`session-${session.id || sessionIndex}`}>{renderFullSessionItem(session)}</React.Fragment>)}
                     </div>
                  })
                  : <div className="FullSessionList_empty">Нет сессий</div>);
        },
        collapseGrouped: ()=>{
            return sessionGroups ? renderCollapseGrouped(sessionGroups) : <Spinner></Spinner>;
        },
        collapseGroupedWithUpcoming: ()=>{
            if (!effectiveSessions){
                return <Spinner></Spinner>;
            }
            const [upcomingGroup, actualGroup] = groupUpcoming();
            // Если мало сессий, показываем простой список
            if (effectiveSessions.length < 2){
                return listModes.single()
            } else if (sessionGroups.length <= 1){
                return listModes.single()
            } 

            // Определяем, есть ли в upcomingGroup хотя бы одна сессия
            const hasUpcomingSessions = upcomingGroup.reduce((acc, group) => acc + group.sessions.length, 0) > 0;

            const renderUpcomingSection = () => {
                if (!hasUpcomingSessions) {
                    return null;
                }

                // Для Upcoming создаем собственный компонент, не используя GroupCollapser
                return (
                    <div className="FullSessionList_group" key="upcoming-group">
                        <DateLine 
                            className="FullSessionList_groupLabel" 
                            onClick={() => setIsUpcomingOpen(!isUpcomingOpen)}
                            label={
                                <>
                                    <span>Upcoming</span>
                                    <DropDownButtonColored 
                                        isActive={true} 
                                        isOpen={isUpcomingOpen}
                                        onChangeOpen={() => {}}
                                    />
                                </>
                            }
                        />
                        {/* Используем обычный div с условным рендерингом вместо Collapser */}
                        {isUpcomingOpen ? (
                            <div className="FullSessionList_collapserGroup">
                                {upcomingGroup.map((group, groupIndex) => (
                                    <div key={`upcoming-month-${groupIndex}`} className="FullSessionList_group">
                                        <DateLineThin 
                                            className="FullSessionList_groupLabel" 
                                            label={new Date(group.date).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                                        />
                                        {group.sessions.map((session, sessionIndex) => 
                                            <React.Fragment key={`upcoming-session-${session.id || sessionIndex}`}>
                                                {renderFullSessionItem(session)}
                                            </React.Fragment>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                );
            };

            return (
                <>
                    {renderUpcomingSection()}
                    {actualGroup.map((group, groupIndex) => {
                        const date = new Date(group.date);
                        return (
                            <GroupCollapser 
                                key={`group-${groupIndex}`} 
                                defaultOpen={false}
                                label={date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                                collapserId={`groupCollapser:date:${date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}:${selectedClient.id}`}
                                isBig={false}
                            >
                                {group.sessions.map((session, sessionIndex) => 
                                    <React.Fragment key={`session-${session.id || sessionIndex}`}>
                                        {renderFullSessionItem(session)}
                                    </React.Fragment>
                                )} 
                            </GroupCollapser>
                        );
                    })}
                </>
            );
        }
    }

    return <div className="FullSessionList">
        <div className="FullSessionList_form">
            {uploadSession && <UploadFileForm 
                key={clickFix} 
                sessionId={uploadSession}
                onProgress={(current, total)=>{
                    /*setProgresses(last=>({
                        ...last, 
                        [uploadSession]: {current, total}
                    }))*/
                }}
                ></UploadFileForm>}
            <div className="FullSessionList_header">
                <div className="FullSessionList_title">
                    Sessions
                </div>
                <div className="button addSession_icon addSession_close_icon" onClick={onClose}></div>
            </div>
            {<div ref={scrollRef} className="FullSessionList_sessions_scroll">
            <GoodScroll>
                <div className="FullSessionList_sessions">
                    {loading ? (
                        <div className="FullSessionList_spinner"><Spinner /></div>
                    ) : (
                        <>
                            {listModes.collapseGroupedWithUpcoming()}
                            {loadingMore && (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                                    <Spinner />
                                </div>
                            )}
                            {hasMore && !loadingMore && effectiveSessions.length > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                                    <button 
                                        className="FullSessionList_loadMoreButton"
                                        onClick={() => loadClientSessions(true)}
                                        disabled={loadingMore}
                                    >
                                        {translate('load_more')}
                                    </button>
                                </div>
                            )}
                            {effectiveSessions.length === 0 && !loading && (
                                <div className="FullSessionList_noSessions">
                                    Client has not any session, create a new one.
                                </div>
                            )}
                        </>
                    )}
                </div>
            </GoodScroll>
            </div>}
            <div className="FullSessionList_buttons">
                <div className="button FullSessionList_add" onClick={()=>onAdd()}>
                    Add session
                </div>
            </div>
        </div>
    </div>
}